import { useEffect, useMemo, useState } from "react";
import { collection, doc, getDocs, limit, orderBy, query, Timestamp, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from '@/firebase';

// 型定義
interface Mail {
  id: string;
  customerEmail: string;
  subject: string;
  status: "open" | "waiting" | "closed" | "processing" | "done" | "spam";
  spamLevel: number;
  lastMessageAt?: Timestamp;
  createdAt?: Timestamp;
  latestDirection?: string;
  latestSendStatus?: string;
  latestFrom?: string;
  latestBodyPreview?: string;
  latestCreatedAt?: Timestamp;
  latestMessageId?: string;
  latestStatus?: string;
}

// Firestore から mail コレクションを取得する関数（クエリ部分を分離）
async function fetchMails(): Promise<Mail[]> {
  const mailRef = collection(db, "mail");
  const mailQuery = query(
    mailRef,
    where("spamLevel", "==", 0),
    orderBy("lastMessageAt", "desc")
  );

  const snapshot = await getDocs(mailQuery);
  const mails = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() as Mail) }));

  const mailMessagesRef = collection(db, "mail_messages");
  const mailsWithLatest = await Promise.all(
    mails.map(async (mail) => {
      const mailDocRef = doc(db, "mail", mail.id);
      const messageQuery = query(
        mailMessagesRef,
        where("mailId", "==", mailDocRef),
        orderBy("createdAt", "desc"),
        limit(1)
      );
      const messageSnapshot = await getDocs(messageQuery);
      const latestDoc = messageSnapshot.docs[0];
      const latest = latestDoc?.data() as
        | {
            direction?: string;
            sendStatus?: string;
            from?: string;
            bodyPreview?: string;
            body?: string;
            createdAt?: Timestamp;
            status?: string;
          }
        | undefined;

      return {
        ...mail,
        latestDirection: latest?.direction,
        latestSendStatus: latest?.sendStatus,
        latestFrom: latest?.from,
        latestBodyPreview: latest?.bodyPreview ?? latest?.body,
        latestCreatedAt: latest?.createdAt,
        latestMessageId: latestDoc?.id,
        latestStatus: latest?.status,
      };
    })
  );

  return mailsWithLatest;
}

function formatDate(timestamp?: Timestamp) {
  if (!timestamp) return "-";
  return timestamp.toDate().toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function StatusPill({ status }: { status: Mail["status"] }) {
  const styles: Record<Mail["status"], string> = {
    open: "bg-green-100 text-green-700",
    waiting: "bg-yellow-100 text-yellow-700",
    closed: "bg-gray-200 text-gray-700",
    processing: "bg-blue-100 text-blue-700",
    done: "bg-emerald-100 text-emerald-700",
    spam: "bg-red-100 text-red-700",
  };

  const labels: Record<Mail["status"], string> = {
    open: "対応中",
    waiting: "返信待ち",
    closed: "完了",
    processing: "処理中",
    done: "処理済み",
    spam: "迷惑メール",
  };

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

type MailFolder = "inbound" | "outbound" | "draft";
type InboundStatusFilter = "all" | "unprocessed" | "processing" | "done" | "spam";

const normalizeDirection = (direction?: string): "inbound" | "outbound" | null => {
  if (!direction) return null;
  const value = direction.toLowerCase();
  if (value === "in" || value === "inbound") return "inbound";
  if (value === "out" || value === "outbound") return "outbound";
  return null;
};

export default function MailListPage() {
  const [mails, setMails] = useState<Mail[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Mail["status"]>("all");
  const [folderFilter, setFolderFilter] = useState<MailFolder>("inbound");
  const [inboundStatusFilter, setInboundStatusFilter] =
    useState<InboundStatusFilter>("all");
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMails();
        if (isMounted) {
          setMails(data);
        }
      } catch (err) {
        console.error("Firestore mail list error:", err);
        if (isMounted) {
          setError("メール一覧の取得に失敗しました。");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredMails = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return mails.filter((mail) => {
      const matchTerm =
        mail.subject.toLowerCase().includes(term) || mail.customerEmail.toLowerCase().includes(term);
      const matchStatus = statusFilter === "all" ? true : mail.status === statusFilter;
      const direction = normalizeDirection(mail.latestDirection);
      const isDraft = mail.latestSendStatus === "draft";
      const inboundStatus =
        mail.latestStatus === "processing"
          ? "processing"
          : mail.latestStatus === "done"
          ? "done"
          : mail.latestStatus === "spam"
          ? "spam"
          : "unprocessed";
      const matchInboundStatus =
        inboundStatusFilter === "all"
          ? true
          : inboundStatusFilter === inboundStatus;
      const matchFolder =
        folderFilter === "inbound"
          ? direction === "inbound" && matchInboundStatus
          : folderFilter === "outbound"
          ? direction === "outbound" && !isDraft
          : direction === "outbound" && isDraft;
      return matchTerm && matchStatus && matchFolder;
    });
  }, [mails, searchTerm, statusFilter, folderFilter, inboundStatusFilter]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set(
        filteredMails.filter((mail) => prev.has(mail.id)).map((mail) => mail.id)
      );
      return next;
    });
  }, [filteredMails]);

  const selectedCount = selectedIds.size;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredMails.map((mail) => mail.id)));
      return;
    }
    setSelectedIds(new Set());
  };

  const toggleSelectOne = (mailId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(mailId);
      } else {
        next.delete(mailId);
      }
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setMails((prev) => prev.filter((mail) => !selectedIds.has(mail.id)));
    setSelectedIds(new Set());
  };

  const allSelected =
    filteredMails.length > 0 && filteredMails.every((mail) => selectedIds.has(mail.id));

  const folderCounts = useMemo(() => {
    return mails.reduce(
      (acc, mail) => {
        const direction = normalizeDirection(mail.latestDirection);
        const isDraft = mail.latestSendStatus === "draft";
        const inboundStatus =
          mail.latestStatus === "processing"
            ? "processing"
            : mail.latestStatus === "done"
            ? "done"
            : mail.latestStatus === "spam"
            ? "spam"
            : "unprocessed";
        if (direction === "inbound") acc.inbound += 1;
        if (direction === "outbound" && !isDraft) acc.outbound += 1;
        if (direction === "outbound" && isDraft) acc.draft += 1;
        if (direction === "inbound") {
          acc.inboundStatus[inboundStatus] += 1;
        }
        return acc;
      },
      {
        inbound: 0,
        outbound: 0,
        draft: 0,
        inboundStatus: {
          unprocessed: 0,
          processing: 0,
          done: 0,
          spam: 0,
        },
      }
    );
  }, [mails]);

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">ホーム &gt; メール管理</p>
            <h1 className="mt-1 text-2xl font-bold text-gray-800">メール管理</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
              onClick={() => navigate("/admin/mail/templates")}
            >
              📄 テンプレート管理
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
              onClick={() => navigate("/admin/mail/new")}
            >
              <span className="text-lg">＋</span> 新規メール作成
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          <aside className="w-64 rounded-xl bg-white p-4 shadow-sm">
            <div className="space-y-3 text-sm text-gray-700">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">フォルダー</p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    setFolderFilter("inbound");
                    setInboundStatusFilter("all");
                  }}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                    folderFilter === "inbound" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-600"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-blue-500">📥</span>
                    <span className="font-semibold">受信メール</span>
                  </span>
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                    {folderCounts.inbound}
                  </span>
                </button>
                {folderFilter === "inbound" && (
                  <div className="ml-6 space-y-1 text-sm">
                    {[
                      { value: "unprocessed" as const, label: "未処理" },
                      { value: "processing" as const, label: "処理中" },
                      { value: "done" as const, label: "処理済み" },
                      { value: "spam" as const, label: "迷惑メール" },
                    ].map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => setInboundStatusFilter(item.value)}
                        className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                          inboundStatusFilter === item.value
                            ? "bg-blue-50 text-blue-700"
                            : "hover:bg-gray-50 text-gray-600"
                        }`}
                      >
                        <span>{item.label}</span>
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                          {folderCounts.inboundStatus[item.value]}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <div className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setFolderFilter("outbound")}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left transition ${
                      folderFilter === "outbound" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span>📤</span>
                      <span className="font-semibold">送信メール</span>
                    </span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                      {folderCounts.outbound}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFolderFilter("draft")}
                    className={`ml-6 flex w-[calc(100%-1.5rem)] items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${
                      folderFilter === "draft" ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span>📝</span>
                      <span>下書き</span>
                    </span>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                      {folderCounts.draft}
                    </span>
                  </button>
                </div>
                <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-600 hover:bg-gray-50">
                  <span>🗑️</span>
                  <span>削除</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-600 hover:bg-gray-50">
                  <span>📁</span>
                  <span>フィルター</span>
                </div>
              </div>
              <hr className="border-dashed" />
              <div className="space-y-2">
                <div className="flex items-center justify-between rounded-lg px-3 py-2 text-gray-600 hover:bg-gray-50">
                  <span className="flex items-center gap-2">
                    <span className="text-blue-500">🔔</span>
                    <span>通知</span>
                  </span>
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">13</span>
                </div>
                <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-gray-600 hover:bg-gray-50">
                  <span className="text-purple-500">💬</span>
                  <span>チャット</span>
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm">
              <button className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                📂 フォルダを選択
              </button>
              <div className="flex items-center gap-2">
                {[
                  { label: "すべて", value: "all" as const },
                  { label: "未読", value: "open" as const },
                  { label: "返信待ち", value: "waiting" as const },
                  { label: "完了", value: "closed" as const },
                ].map((chip) => (
                  <button
                    key={chip.value}
                    onClick={() => setStatusFilter(chip.value)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      statusFilter === chip.value
                        ? "bg-blue-600 text-white shadow-sm"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-2">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  <input
                    type="text"
                    placeholder="検索: 件名・メールアドレス"
                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-blue-400 focus:bg-white focus:outline-none"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                  🧭 スマートリプライ
                </button>
              </div>
            </div>

            <div className="rounded-xl bg-white shadow-sm">
              <div className="flex items-center justify-between border-b px-4 py-3 text-sm text-gray-600">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      checked={allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                    />
                    選択
                  </label>
                  <button
                    className="rounded border border-gray-200 px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                    onClick={handleBulkDelete}
                    disabled={selectedCount === 0}
                  >
                    削除 {selectedCount > 0 ? `(${selectedCount})` : ""}
                  </button>
                  <button className="rounded border border-gray-200 px-3 py-1.5 hover:bg-gray-50">もっと見る</button>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <button className="rounded border border-gray-200 px-3 py-1.5 hover:bg-gray-50">📅</button>
                  <span>|</span>
                  <select className="rounded border border-gray-200 px-3 py-1.5 text-sm focus:outline-none">
                    <option>古い順</option>
                    <option>新しい順</option>
                  </select>
                </div>
              </div>

              <div className="divide-y">
                {filteredMails.map((mail) => (
                  <button
                    key={mail.id}
                    className="w-full px-4 py-3 text-left transition hover:bg-blue-50"
                    onClick={() => {
                      if (!mail.latestMessageId) return;
                      navigate(`/admin/mail/${mail.id}/messages/${mail.latestMessageId}`);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="pt-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedIds.has(mail.id)}
                          onChange={(e) => toggleSelectOne(mail.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">
                        {mail.customerEmail[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <StatusPill status={mail.status} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800 truncate">{mail.subject}</span>
                        </div>
                        <p className="line-clamp-2 text-sm text-gray-600">
                          {truncateText(mail.latestBodyPreview || "", 140)}
                        </p>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>From: {mail.latestFrom || "-"}</div>
                        <div>受信: {formatDate(mail.latestCreatedAt)}</div>
                      </div>
                    </div>
                  </button>
                ))}

                {filteredMails.length === 0 && (
                  <div className="px-4 py-6 text-center text-sm text-gray-500">
                    対象のメールがありません。
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
